/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: ObjectBaseComponent
 * Reason: Panel component that renders the full LogicFlowTrace detail view
 *         (summary card, variable store matrix, per-Logic counter widget) for
 *         a given trace id. Used as the right-hand side of the master/detail
 *         layout in the LG flow object page 'testlaeufe' section. Replaces
 *         the deep-link route to LgFlowTraceDetailPage.
 *
 * TM-1000001563 NOTE: in this panel, executionId == LogicFlowTrace.id (not
 * LgFlowExecution.id). The synthetic adapter `childTracesAsResultDto` maps
 * LogicFlowTrace.id into the `executionId` slot of ChildExecutionSummaryDto
 * so the shared <mvs-lg-flow-execution-tree-panel> can render the hierarchy.
 * We DO NOT call LgFlowVisualizerService#getTraceForExecution here — that
 * endpoint is for live LgFlowExecution rows; this panel works on persisted
 * LogicFlowTrace rows via the lg.LogicFlowTrace / lg.LogicFlowTraceStep CRUD
 * services.
 *
 * @ticket TM-1000001563
 * Object-Base-Component for lg.LogicFlowTrace. Loads via
 * objectIdentifier.objectId provided by the navigation service.
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {Subscription} from 'rxjs';

import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
// TM-1000001563: PageService exposes `breadcrumbHidden` so this OBC can
// suppress the global object-navigation breadcrumb on the new-tab detail
// page (where the OBC owns its own header chrome).
import {PageService} from '../../../../../core/shared/service/page/page.service';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {ObjectRequestList} from '../../../../../core/shared/dto/object/request/object-request-list';
import {DtoList} from '../../../../../core/shared/dto/dto.list';
import {QlQueryDto} from '../../../../../core/shared/ql/data/dto/ql-query.dto';
import {QlService} from '../../../../../core/shared/ql/service/ql.service';
// TM-1000001563: navigation imports for the cmd/ctrl-click 'open Logic' handler.
import {MvsObjectNavigationService} from '../../../../../core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from '../../../../../core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from '../../../../../core/shared/navigation/mvs-object-navigation-action-enum';

import {LogicFlowTraceDto} from '../../../model/dto/entity/logic-flow-trace.dto';
import {LogicFlowTraceStepDto} from '../../../model/dto/entity/logic-flow-trace-step.dto';
import {LogicFlowDto} from '../../../model/dto/entity/logic-flow.dto';
import {LogicFlowStepDto} from '../../../model/dto/entity/logic-flow-step.dto';
import {LogicFlowStepExecutionStatusEnum} from '../../../enum/logic-flow-step-execution-status.enum';
// TM-1000001563: SUB_FLOW step-type detection for canvas click drill-in.
import {LogicFlowStepTypeEnum} from '../../../enum/logic-flow-step-type.enum';
// TM-1000001563: replaces removed boolean trace.success when projecting trace state into LgFlowTraceResultDto.
import {LogicFlowTraceStatusEnum} from '../../../enum/logic-flow-trace-status.enum';
import {LgFlowStepTraceDto} from '../../../model/dto/api/lg-flow-step-trace.dto';
import {LogicFlowImportDto} from '../../../model/dto/entity/logic-flow-import.dto';
import {LogicFlowExportDto} from '../../../model/dto/entity/logic-flow-export.dto';
// TM-1000001563: imports for resolver-driven canvas (sub-flow nodes + declarations)
import {
    LogicFlowInheritanceResolverService,
    ResolvedFlowStep,
} from '../../../service/inheritance/logic-flow-inheritance-resolver.service';
import {LogicFlowResolvedDeclarationDto} from '../../../model/dto/api/logic-flow-resolved-declaration.dto';
import {LgFlowTraceResultDto} from '../../../model/dto/api/lg-flow-trace-result.dto';
import {ChildExecutionSummaryDto} from '../../../model/dto/api/child-execution-summary.dto';
// TM-1000001563: step bindings for the sidebar Eingabe / Ausgabe rows.
import {LogicFlowStepBindingDto} from '../../../model/dto/entity/logic-flow-step-binding.dto';
import {BindingDirectionEnum} from '../../../enum/binding-direction.enum';
// TM-1000001563: pure helper that builds the alias-bookkeeping maps.
import {AliasMaps, buildAliasMaps} from '../../../util/logic-flow-alias-bookkeeping';
// TM-1000001563: typed selection payload for the flow-I/O detail panel.
import {LgFlowIoSelection} from '../lg-flow-io-detail-panel/lg-flow-io-detail-panel.component';

/**
 * @ticket TM-1000001563
 * Raw row shape returned by the QL `object` pipe for `lg.LogicFlowStepBinding`.
 * Mirrors the shape used in {@link LgFlowTestRunnerComponent} so the sidebar
 * sees identical pre-run binding data on both surfaces.
 */
// TM-1000001563: raw QL row shape for LogicFlowStepBinding.
interface FlowStepBindingRawRow {
    id: number;
    flowStepId: number;
    direction: number;
    variableName: string;
}

/**
 * @ticket TM-1000001563
 * Atomic per-level snapshot of everything a single canvas level needs in
 * order to render: the LogicFlow header, the step list, the resolved
 * inheritance picture, declared imports/exports, and resolved declarations.
 * Cached by flowId in {@link flowDefinitionCache} so two distinct sub-flows
 * never pollute each other's resolved data.
 */
// TM-1000001563: per-flow definition snapshot used for atomic level swaps.
interface FlowDefinition {
    flow: LogicFlowDto;
    steps: LogicFlowStepDto[];
    resolvedSteps: ResolvedFlowStep[];
    overrideMap?: Map<number, number>;
    imports: LogicFlowImportDto[];
    exports: LogicFlowExportDto[];
    declarations: LogicFlowResolvedDeclarationDto[];
    declarationsExist: boolean;
}

@Component({
    selector: 'mvs-lg-flow-trace-detail-panel',
    templateUrl: './lg-flow-trace-detail-panel.component.html',
    styleUrls: ['./lg-flow-trace-detail-panel.component.scss'],
    standalone: false
})
export class LgFlowTraceDetailPanelComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    /**
     * @ticket TM-1000001563
     * Narrowed dto field for the lg.LogicFlowTrace OBC contract. Populated
     * by the base class after `crudService.get(objectIdentifier.objectId)`
     * resolves; consumed by `onObjectChanged()` as the root trace.
     */
    override dto: LogicFlowTraceDto;

    // ----------------------------------------------------------------
    // ROOT-LEVEL DATA — drives summary card + variable store matrix.
    // These NEVER change on sub-flow drill-in (the summary card and
    // variable store always reflect the persisted root trace).
    // ----------------------------------------------------------------

    protected trace: LogicFlowTraceDto;
    protected traceFlowInputs: Record<string, any> = {};
    protected traceFlowOutputs: Record<string, any> = {};
    protected traceStepTraces: LgFlowStepTraceDto[] = [];

    // Flow definition (for read-only canvas rendering)
    protected logicFlow: LogicFlowDto | null = null;
    protected flowSteps: LogicFlowStepDto[] = [];

    /** Declared import definitions of the ROOT trace's flow. */
    // TM-1000001563: declared import definitions for the variable store matrix
    protected flowImportsList: LogicFlowImportDto[] = [];

    /** Declared export definitions of the ROOT trace's flow. */
    // TM-1000001563: declared export definitions for the variable store matrix
    protected flowExportsList: LogicFlowExportDto[] = [];

    // ----------------------------------------------------------------
    // PER-LEVEL VARIABLE-MATRIX + COUNTER STATE (TM-1000001563, Issue 5)
    // ----------------------------------------------------------------
    // The variable matrix and the per-Logic counter widget below the
    // canvas USED to always show the ROOT trace's data, even after a
    // SUB_FLOW drill-in. With Issue 5 they now follow the active canvas
    // level: on root they mirror the trace fields above, on a sub-flow
    // level they reflect the resolved child `LogicFlowTrace` row (looked
    // up by `parentTrace == levelTraceId AND logicFlow == subFlowDtoId`).
    // If no persisted child trace exists (legacy data), `levelTrace`
    // stays null and the warning chip in the template is shown; the
    // matrix uses the existing `subFlowIoFromRoot(...)` fallback so the
    // user still sees the IO that flowed in/out of the SUB_FLOW step.
    // ----------------------------------------------------------------

    /**
     * @ticket TM-1000001563
     * Resolved child `LogicFlowTrace` for the active canvas level — null
     * on the root level (the variable matrix uses the root trace there)
     * AND on sub-flow levels where no persisted child trace exists.
     */
    // TM-1000001563: resolved child trace at the active canvas level (Issue 5).
    protected levelTrace: LogicFlowTraceDto | null = null;

    /**
     * @ticket TM-1000001563
     * Trace id of the active canvas level — feeds the per-Logic counter
     * widget filter so it shows invocations from THIS trace level only.
     */
    // TM-1000001563: trace id of the active level (Issue 5).
    protected levelTraceId: number | null = null;

    /**
     * @ticket TM-1000001563
     * Per-step trace rows of the active canvas level — feeds the
     * variable matrix.
     */
    // TM-1000001563: step traces of the active level (Issue 5).
    protected levelStepTraces: LgFlowStepTraceDto[] = [];

    /**
     * @ticket TM-1000001563
     * Flow-level input values at the active canvas level — feeds the
     * variable matrix's IMPORT (sign-in) rows.
     */
    // TM-1000001563: flow-IO inputs at the active level (Issue 5).
    protected levelFlowInputs: Record<string, any> = {};

    /**
     * @ticket TM-1000001563
     * Flow-level output values at the active canvas level — feeds the
     * variable matrix's EXPORT (sign-out) rows.
     */
    // TM-1000001563: flow-IO outputs at the active level (Issue 5).
    protected levelFlowOutputs: Record<string, any> = {};

    /**
     * @ticket TM-1000001563
     * True when the canvas is on a sub-flow level for which we could not
     * resolve a persisted child trace. Drives the warning chip in the
     * template explaining that no detailed protocol is available.
     */
    // TM-1000001563: warning flag for sub-flows without persisted child trace.
    protected levelTraceMissing: boolean = false;

    /**
     * @ticket TM-1000001563
     * Cached snapshot of root-level matrix data so {@link navigateToRoot}
     * can restore it cheaply when the user clicks "Zurück" / a tree node
     * pointing to root, instead of re-resolving root state.
     */
    // TM-1000001563: cached root matrix data for zero-cost restore.
    private rootMatrixSnapshot: {
        trace: LogicFlowTraceDto | null;
        traceId: number | null;
        stepTraces: LgFlowStepTraceDto[];
        flowInputs: Record<string, any>;
        flowOutputs: Record<string, any>;
    } | null = null;

    // ----------------------------------------------------------------
    // PER-LEVEL CANVAS STATE (TM-1000001563)
    // The canvas <lg-flow-designer> renders the level identified by
    // `currentTraceId`. On root, currentTraceId === traceId. On drill-in,
    // currentTraceId points at a child LogicFlowTrace and the per-level
    // fields below are populated for that trace's flow definition.
    // ----------------------------------------------------------------

    /** TraceId currently rendered on the canvas. */
    // TM-1000001563: trace id of the active canvas level.
    protected currentTraceId: number | null = null;

    /** Root trace id — the `objectIdentifier.objectId` at the time of the last reset. */
    // TM-1000001563: cached root traceId for drill-out detection.
    protected rootTraceId: number | null = null;

    /**
     * @ticket TM-1000001563
     * Chain of `LogicFlowStep` ids of the SUB_FLOW steps we drilled THROUGH
     * to reach the active canvas level. Empty means the canvas is on the
     * root trace level. Each push corresponds to a "drill into sub-flow"
     * action; "Zurück" pops the deepest entry.
     *
     * The Ausführungsbaum is built entirely from step definitions + the
     * root trace's SUB_FLOW step traces, so we do NOT need any persisted
     * child LogicFlowTrace data to navigate the tree.
     */
    // TM-1000001563: chain of SUB_FLOW step ids that led us into the current sub-flow.
    protected viaStepStack: number[] = [];

    /** LogicFlow entity of the active level. */
    // TM-1000001563: per-level flow header.
    protected currentFlow: LogicFlowDto | null = null;

    /** Step definitions of the active level's flow. */
    // TM-1000001563: per-level step definitions (canvas nodes).
    protected currentFlowSteps: LogicFlowStepDto[] = [];

    /** Resolved inheritance steps for the active level. */
    // TM-1000001563: per-level resolved inheritance steps.
    protected currentResolvedSteps: ResolvedFlowStep[] = [];

    /** Override map for the active level's resolved inheritance. */
    // TM-1000001563: per-level inheritance override map.
    protected currentOverrideMap?: Map<number, number>;

    /** Declared LogicFlowImport rows of the active level. */
    // TM-1000001563: per-level declared imports (Start card schema).
    protected currentFlowImports: LogicFlowImportDto[] = [];

    /** Declared LogicFlowExport rows of the active level. */
    // TM-1000001563: per-level declared exports (End card schema).
    protected currentFlowExports: LogicFlowExportDto[] = [];

    /** Resolved declarations for the active level. */
    // TM-1000001563: per-level resolved declarations (rail + glow).
    protected currentDeclarations: LogicFlowResolvedDeclarationDto[] = [];

    /** Drives the canvas toolbar 'Deklarationen' button visibility. */
    // TM-1000001563: per-level 'any declarations exist?' flag.
    protected currentDeclarationsExist: boolean = false;

    /** Step ids that call the currently selected alias on the active level. */
    // TM-1000001563: per-level alias glow set.
    protected currentCallingStepIdsForAlias: Set<number> | null = null;

    /** Step traces of the active level — feeds the canvas execution overlay. */
    // TM-1000001563: per-level step traces (canvas execution overlay).
    protected currentStepTraces: LgFlowStepTraceDto[] = [];

    /**
     * @ticket TM-1000001563
     * Flow-level inputs/outputs of the ACTIVE canvas level. Drives the START
     * and ENDE pseudo-nodes on the canvas + the flow-IO detail panel.
     *   - On root level → mirrors `traceFlowInputs`/`traceFlowOutputs`.
     *   - On a sub-flow level → sourced from the parent SUB_FLOW step's
     *     persisted `inputDataJson`/`outputDataJson` (i.e. the values that
     *     were passed into/out of the sub-flow during the parent's run).
     *     Identical to what the live designer shows because both ultimately
     *     read the same SUB_FLOW step trace data.
     *   - At depth > 1 → empty `{}` because we have no per-step trace data
     *     for nested sub-flows on this surface.
     */
    // TM-1000001563: per-level flow-IO (drives canvas START/ENDE pseudo-nodes).
    protected currentFlowInputs: Record<string, any> = {};
    protected currentFlowOutputs: Record<string, any> = {};

    /**
     * Whether the right-hand declaration rail is currently visible. Open/closed
     * state is shared across levels — toggling on a sub-flow keeps it open on
     * drill-out, mirroring the live runner's behaviour.
     */
    // TM-1000001563: open/closed state for the declaration rail panel
    protected declarationsViewOpen: boolean = false;

    // ----------------------------------------------------------------
    // CACHES (TM-1000001563)
    // ----------------------------------------------------------------

    /** Cached, fully-resolved flow definitions keyed by flow id. */
    // TM-1000001563: per-flowId atomic snapshot to keep level swaps clean.
    private flowDefinitionCache: Map<number, FlowDefinition> = new Map();

    /** In-flight flow-definition fetches — guards against double-fetches. */
    // TM-1000001563: loading guard for flow-definition fetches.
    private loadingFlowIds: Set<number> = new Set();

    // ----------------------------------------------------------------
    // TM-1000001563: alias bookkeeping (Defect 2 — declaration click → glow)
    // ----------------------------------------------------------------

    /**
     * @ticket TM-1000001563
     * Currently selected alias on the declarations rail — drives the canvas
     * glow on calling steps. Mirrors the page-level `selectedAliasForGlow`.
     */
    // TM-1000001563: alias selected on the rail (canvas glow target).
    protected selectedAlias: string | null = null;

    /**
     * @ticket TM-1000001563
     * Alias bookkeeping maps for the active canvas level. Rebuilt whenever
     * the active level's `currentResolvedSteps` + `currentDeclarations` land.
     */
    // TM-1000001563: alias bookkeeping (per-level, delegated to pure helper).
    private aliasMaps: AliasMaps = {
        calledAliasesByStep: new Map<number, Set<string>>(),
        dynamicAliasCallsByStep: new Map<number, number>(),
        aliasToCallingStepIds: new Map<string, Set<number>>(),
    };

    // ----------------------------------------------------------------
    // TM-1000001563: step-detail sidebar state (Defect 3 — extracted sidebar host)
    // ----------------------------------------------------------------

    /** Step trace whose values populate the sidebar Eingabe / Ausgabe rows. */
    // TM-1000001563: selected step trace (post-run side).
    protected selectedStepTrace: LgFlowStepTraceDto | null = null;
    /** Step definition behind the open sidebar — drives Beschreibung + Editor. */
    // TM-1000001563: selected step definition (also drives Editor / Mappings).
    protected selectedStepDef: LogicFlowStepDto | null = null;
    /** Whether the right-side sidebar is open. */
    // TM-1000001563: sidebar open flag.
    protected sidebarOpen: boolean = false;
    /** Active mode in the sidebar (Schritt-Details vs Logik-Deklarationen). */
    // TM-1000001563: sidebar mode (mirror of test-runner's local field).
    protected sidebarMode: 'step' | 'declarations' = 'step';
    /**
     * Bindings of the ACTIVE level's flow grouped by step id. Rebuilt on each
     * level swap so the sidebar always renders bindings of the current canvas.
     */
    // TM-1000001563: per-level step bindings cache for the sidebar.
    protected stepBindingsByStepId: Map<number, LogicFlowStepBindingDto[]> = new Map();

    /** Active flow-I/O selection (START / ENDE click). */
    // TM-1000001563: flow-I/O selection state — mutually exclusive with the step sidebar.
    protected selectedFlowIo: LgFlowIoSelection | null = null;

    /** Pre-filtered bindings of the selected step — fed into the sidebar. */
    // TM-1000001563: feed the extracted sidebar with the selected step's bindings.
    get selectedStepBindings(): LogicFlowStepBindingDto[] {
        const stepId = this.selectedStepDef?.id ?? -1;
        return this.stepBindingsByStepId.get(stepId) ?? [];
    }

    /**
     * @ticket TM-1000001563
     * Aliases the currently selected step calls — drives the inline
     * Logik-Deklarationen list inside the sidebar.
     */
    // TM-1000001563: called aliases for the currently selected step.
    private get calledAliasesForSelectedStep(): string[] {
        if (!this.selectedStepDef) return [];
        const set = this.aliasMaps.calledAliasesByStep.get(this.selectedStepDef.id);
        return set ? Array.from(set) : [];
    }

    /** Number of dynamic `$ctx.executeLogic(...)` calls in the selected step. */
    // TM-1000001563: dynamic-call counter for the inline declaration footer.
    get dynamicCallsForSelectedStep(): number {
        if (!this.selectedStepDef) return 0;
        return this.aliasMaps.dynamicAliasCallsByStep.get(this.selectedStepDef.id) ?? 0;
    }

    /** Resolved declarations the selected step calls. */
    // TM-1000001563: full resolved declarations for the selected step's static calls.
    get calledDeclarationsForSelectedStep(): LogicFlowResolvedDeclarationDto[] {
        const aliases = this.calledAliasesForSelectedStep;
        if (aliases.length === 0 || !this.currentDeclarations) return [];
        const byAlias = new Map<string, LogicFlowResolvedDeclarationDto>();
        for (const decl of this.currentDeclarations) {
            byAlias.set(decl.alias, decl);
        }
        const out: LogicFlowResolvedDeclarationDto[] = [];
        for (const alias of aliases) {
            const decl = byAlias.get(alias);
            if (decl) out.push(decl);
        }
        return out;
    }

    /** Aliases the selected step calls but that have NO matching declaration. */
    // TM-1000001563: unresolved alias list for the inline section's red rows.
    get unresolvedCalledAliasesForSelectedStep(): string[] {
        const aliases = this.calledAliasesForSelectedStep;
        if (aliases.length === 0) return [];
        const declared = new Set<string>(this.currentDeclarations?.map(d => d.alias) ?? []);
        return aliases.filter(a => !declared.has(a));
    }

    // Widgets
    protected counterWidget: WidgetData;

    // Subscriptions
    private subscriptions: Subscription[] = [];

    /**
     * @ticket TM-1000001563
     * OBC contract dependencies (coreService / messageService /
     * confirmationService / observerService) are forwarded to
     * {@link ObjectBaseComponent}. Module-specific services
     * (inheritanceResolver / qlService / navigationService) remain
     * panel-local.
     */
    constructor(
        protected override coreService: MvsCoreService,
        protected override messageService: MvsMessageService,
        protected override confirmationService: ConfirmationService,
        protected override observerService: ObserverService,
        // TM-1000001563: re-resolves inheritance + declarations for the
        // traced flow so the read-only canvas matches the live designer.
        private inheritanceResolver: LogicFlowInheritanceResolverService,
        // TM-1000001563: QL service used to load the per-flow step bindings.
        private qlService: QlService,
        // TM-1000001563: navigation service for cmd/ctrl-click → open Logic.
        private navigationService: MvsObjectNavigationService,
        // TM-1000001563: toggle the global breadcrumb off while this OBC is
        // mounted (it owns its own summary header).
        private pageService: PageService,
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    /**
     * @ticket TM-1000001563
     * Standard OBC lifecycle hook — delegates to the base class so the
     * trace is fetched via `crudService.get(objectIdentifier.objectId)`.
     * Also toggles `pageService.breadcrumbHidden` so the AppMainComponent
     * suppresses the overlay breadcrumb while this OBC is the active view.
     */
    override ngOnInit(): void {
        // TM-1000001563: hide the global object-navigation breadcrumb.
        this.pageService.breadcrumbHidden = true;
        super.ngOnInit();
    }

    /**
     * @ticket TM-1000001563
     * Standard OBC change-detection plumbing — re-runs the base load
     * pipeline when objectIdentifier flips.
     */
    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
    }

    /**
     * @ticket TM-1000001563
     * Called by {@link ObjectBaseComponent} AFTER the lg.LogicFlowTrace
     * CRUD `get(objectIdentifier.objectId)` resolves and `this.dto` is
     * populated. Drives the same data-acquisition that the legacy
     * `ngOnChanges(changes['traceId'])` block used to do — but reuses
     * `this.dto` as the root trace instead of re-fetching it.
     *
     * Side effects preserved:
     *  - canvas data (currentFlow / currentFlowSteps / currentResolvedSteps),
     *  - variable matrix (level* fields, rootMatrixSnapshot),
     *  - per-Logic counter widget (refreshCounterWidget),
     *  - drill-in stack init (viaStepStack reset, currentTraceId seed).
     *
     * MUST NOT call refreshObject() here — that would re-enter the OBC
     * load pipeline and trigger an infinite loop.
     */
    override onObjectChanged(): void {
        // TM-1000001563: clear all per-level state from a previous trace.
        this.resetState();

        if (!this.dto || this.dto.id == null) {
            return;
        }

        // TM-1000001563: seed the root level using the dto provided by the
        // OBC base. navigateToRoot() then loads steps + flow definition and
        // applies them atomically to the current* / level* fields.
        this.rootTraceId = this.dto.id;
        this.navigateToRoot();
        this.refreshCounterWidget();
    }

    /**
     * @ticket TM-1000001563
     * Standard OBC teardown — also flushes the panel-local subscriptions
     * collected by the per-level loaders (resetState() / navigateToRoot())
     * and restores the global breadcrumb visibility.
     */
    override ngOnDestroy(): void {
        // TM-1000001563: restore the global breadcrumb for the next view.
        this.pageService.breadcrumbHidden = false;
        for (const sub of this.subscriptions) {
            sub.unsubscribe();
        }
        this.subscriptions = [];
        super.ngOnDestroy();
    }

    private resetState(): void {
        // Root-level data
        this.trace = null;
        this.traceFlowInputs = {};
        this.traceFlowOutputs = {};
        this.traceStepTraces = [];
        this.logicFlow = null;
        this.flowSteps = [];
        this.flowImportsList = [];
        this.flowExportsList = [];
        this.counterWidget = null;

        // TM-1000001563 (Issue 5): per-level variable-matrix + counter state.
        this.levelTrace = null;
        this.levelTraceId = null;
        this.levelStepTraces = [];
        this.levelFlowInputs = {};
        this.levelFlowOutputs = {};
        this.levelTraceMissing = false;
        this.rootMatrixSnapshot = null;

        // TM-1000001563: per-level canvas state
        this.currentTraceId = null;
        this.rootTraceId = null;
        this.viaStepStack = [];
        this.currentFlow = null;
        this.currentFlowSteps = [];
        this.currentResolvedSteps = [];
        this.currentOverrideMap = undefined;
        this.currentFlowImports = [];
        this.currentFlowExports = [];
        this.currentDeclarations = [];
        this.currentDeclarationsExist = false;
        this.currentCallingStepIdsForAlias = null;
        this.currentStepTraces = [];
        this.currentFlowInputs = {};
        this.currentFlowOutputs = {};

        // TM-1000001563: caches
        this.flowDefinitionCache = new Map();
        this.loadingFlowIds = new Set();

        // TM-1000001563: memoized tree adapter — reset to stable empty
        // references so the OnPush tree-panel doesn't render stale data
        // before the new root level lands.
        this.treeRootResult = null;
        this.treeChildCache = new Map();

        // TM-1000001563: declarations / sidebar / alias state
        this.declarationsViewOpen = false;
        this.selectedAlias = null;
        this.aliasMaps = {
            calledAliasesByStep: new Map<number, Set<string>>(),
            dynamicAliasCallsByStep: new Map<number, number>(),
            aliasToCallingStepIds: new Map<string, Set<number>>(),
        };
        this.selectedStepTrace = null;
        this.selectedStepDef = null;
        this.sidebarOpen = false;
        this.sidebarMode = 'step';
        this.stepBindingsByStepId = new Map();
        this.selectedFlowIo = null;

        // TM-1000001563: cancel any in-flight subscriptions before re-seeding
        for (const sub of this.subscriptions) {
            sub.unsubscribe();
        }
        this.subscriptions = [];
    }

    // ----------------------------------------------------------------
    // TM-1000001563: NAVIGATION ORCHESTRATORS
    // ----------------------------------------------------------------

    /**
     * @ticket TM-1000001563
     * Seed (or re-seed) the canvas with the ROOT trace level. Loads the
     * `LogicFlowTrace` + its `LogicFlowTraceStep` rows AND the flow
     * definition; applies them atomically so the canvas never renders
     * mixed-level data.
     *
     * The Ausführungsbaum is built from `flowSteps` ∩ `traceStepTraces`
     * filtered to SUB_FLOW step types (see `childTracesAsResultDto`) — no
     * persisted child traces are needed.
     */
    // TM-1000001563: atomic root-level seed (load → gate → apply).
    private navigateToRoot(): void {
        const traceId = this.rootTraceId;
        if (traceId == null) return;

        // TM-1000001563: triple-gate so the apply step runs ONLY when ALL
        // three independent fetches (trace header, trace steps, flow
        // definition) have produced data. An earlier "implicit" two-gate
        // version had a race: when a user clicks "Zurück" out of a sub-flow,
        // the root flow definition is already cached so flowLoaded flips
        // synchronously; if the steps fetch is still in flight, the
        // two-gate maybeApply would fire with empty steps and clobber
        // `this.traceStepTraces`, which in turn made `treePanelVisible`
        // return false and the Ausführungsbaum disappear.
        let traceLoaded = false;
        let stepsLoaded = false;
        let flowLoaded = false;
        let loadedTrace: LogicFlowTraceDto | null = null;
        let loadedSteps: LogicFlowTraceStepDto[] = [];
        let loadedDef: FlowDefinition | null = null;

        const maybeApply = () => {
            if (!traceLoaded || !stepsLoaded || !flowLoaded) return;
            if (!loadedTrace || !loadedDef) return;

            // TM-1000001563: at root level the active canvas IS the trace,
            // so currentTraceId === rootTraceId and viaStepStack stays empty.
            this.viaStepStack = [];
            this.currentTraceId = traceId;

            // Populate root-level data (drives summary card + variable
            // store) AND per-level current* fields (drives canvas).
            this.trace = loadedTrace;
            this.traceFlowInputs = this.safeParseJson(loadedTrace.flowInputsJson);
            this.traceFlowOutputs = this.safeParseJson(loadedTrace.flowOutputsJson);
            this.traceStepTraces = this.mapTraceSteps(loadedSteps);
            this.logicFlow = loadedDef.flow;
            this.flowSteps = loadedDef.steps;
            this.flowImportsList = loadedDef.imports;
            this.flowExportsList = loadedDef.exports;

            this.applyFlowDefinitionToCurrent(loadedDef);
            this.currentStepTraces = this.traceStepTraces;
            // TM-1000001563: at root level the canvas's START/ENDE
            // pseudo-nodes show the trace's flow-IO directly.
            this.currentFlowInputs = this.traceFlowInputs;
            this.currentFlowOutputs = this.traceFlowOutputs;

            // TM-1000001563 (Issue 5): seed the per-level matrix + counter
            // fields with the root trace's data. Snapshot the result so
            // navigateToRoot() called from "Zurück" / tree-root click can
            // restore it without re-fetching.
            this.levelTrace = loadedTrace;
            this.levelTraceId = traceId;
            this.levelStepTraces = this.traceStepTraces;
            this.levelFlowInputs = this.traceFlowInputs;
            this.levelFlowOutputs = this.traceFlowOutputs;
            this.levelTraceMissing = false;
            this.rootMatrixSnapshot = {
                trace: this.levelTrace,
                traceId: this.levelTraceId,
                stepTraces: this.levelStepTraces,
                flowInputs: this.levelFlowInputs,
                flowOutputs: this.levelFlowOutputs,
            };
            // TM-1000001563 (Issue 5): rebuild the counter widget against
            // the root trace's id (the existing call from ngOnChanges
            // already does this on first load; this assignment keeps the
            // re-entry path — tree-root click after a drill — correct).
            this.refreshCounterWidget();

            // TM-1000001563: rebuild memoized tree adapter fields now that
            // root trace + steps + flow definition are in place. Stable
            // references prevent the OnPush tree-panel from looping.
            this.recomputeTreeData();
        };

        // 1. Trace header — provided by the OBC base via `this.dto`, no
        //    refetch needed. Triggers flow-definition load once flowId known.
        // TM-1000001563: OBC migration — root trace comes from `this.dto`.
        loadedTrace = this.dto ?? null;
        traceLoaded = true;
        if (loadedTrace?.logicFlowDtoId != null) {
            this.loadFlowDefinition(loadedTrace.logicFlowDtoId, (def) => {
                loadedDef = def;
                flowLoaded = true;
                maybeApply();
            });
        } else {
            // Defensive fallback — render an empty canvas if the
            // trace has no flow id (legacy data).
            loadedDef = this.makeEmptyFlowDefinition();
            flowLoaded = true;
            maybeApply();
        }

        // 2. Trace steps — independent fetch; gates the apply via stepsLoaded.
        const stepsRequest = ObjectRequestList.createBasic(
            true,
            [FilterCriteria.create('trace', FilterCriteria.cOperatorEqual, traceId)],
            [new Sorting('executionOrder', true)]
        );
        const stepsCrud = this.coreService.getCrudService('lg.LogicFlowTraceStep');
        const stepsSub = stepsCrud.list(stepsRequest).subscribe({
            next: (result: DtoList<LogicFlowTraceStepDto>) => {
                loadedSteps = (result?.entries ?? []) as LogicFlowTraceStepDto[];
                stepsLoaded = true;
                maybeApply();
            },
            error: () => {
                loadedSteps = [];
                stepsLoaded = true;
                maybeApply();
            }
        });
        this.subscriptions.push(stepsSub);
    }

    /**
     * @ticket TM-1000001563
     * Drill the canvas into the sub-flow referenced by the given SUB_FLOW
     * step. No persisted child trace is required — we just load the
     * sub-flow's static definition and swap the canvas to render its
     * structure. The execution overlay turns OFF on sub-levels because we
     * have no per-step trace data there.
     *
     * `viaStepStack` records the chain of SUB_FLOW step ids we traversed
     * so "Zurück" can pop one level at a time.
     */
    // TM-1000001563: drill into a SUB_FLOW step's sub-flow (no trace data needed).
    private drillIntoSubFlow(stepDefId: number): void {
        const stepDef = this.findStepDefById(stepDefId);
        if (!stepDef || stepDef.subFlowDtoId == null) return;

        // TM-1000001563: BEFORE we push onto the via-stack, capture the
        // values that were passed INTO and OUT OF the sub-flow during the
        // root execution. The data lives in the parent SUB_FLOW step's
        // trace step row (its inputDataJson / outputDataJson). We only
        // have this when drilling FROM the root level (one level deep);
        // at depths > 1 we don't have any sub-trace data so the canvas's
        // START/ENDE pseudo-nodes show empty values, which is the correct
        // representation of "we don't know".
        const subFlowInputs: Record<string, any> = this.viaStepStack.length === 0
            ? this.subFlowIoFromRoot(stepDefId, 'inputs')
            : {};
        const subFlowOutputs: Record<string, any> = this.viaStepStack.length === 0
            ? this.subFlowIoFromRoot(stepDefId, 'outputs')
            : {};

        // TM-1000001563 (Issue 5): the trace id at the level we are
        // drilling FROM (the parent of the new level). The child trace
        // lookup uses this as its `parentTrace = ?` filter so we resolve
        // the correct nested LogicFlowTrace row even when drilling
        // through multiple sub-flows.
        const parentLevelTraceId = this.levelTraceId;

        this.loadFlowDefinition(stepDef.subFlowDtoId, (def) => {
            // TM-1000001563: push the step we drilled through so the chip
            // and "Zurück" know how to navigate back. Then apply the new
            // sub-flow definition atomically.
            this.viaStepStack = [...this.viaStepStack, stepDefId];
            this.currentTraceId = null; // sub-levels have no trace
            this.applyFlowDefinitionToCurrent(def);
            // Sub-level has no per-step execution data — the canvas
            // renders the static structure but DOES surface the
            // sub-flow's flow-IO via START/ENDE pseudo-nodes when we
            // captured them above.
            this.currentStepTraces = [];
            this.currentFlowInputs = subFlowInputs;
            this.currentFlowOutputs = subFlowOutputs;

            // TM-1000001563 (Issue 5): also re-resolve the variable
            // matrix + counter widget against the persisted child trace
            // (if any) for the sub-flow we are entering. Falls back to
            // the existing subFlowIoFromRoot(...) data when no child
            // trace row exists (legacy data) — see resolveLevelTrace.
            this.resolveLevelTrace(
                parentLevelTraceId,
                stepDef.subFlowDtoId!,
                subFlowInputs,
                subFlowOutputs,
            );
        });
    }

    /**
     * @ticket TM-1000001563 (Issue 5)
     * Resolve the child `LogicFlowTrace` for the given sub-flow level and
     * apply it to the per-level matrix + counter fields. The child row is
     * looked up by:
     *   - `parentTrace == parentLevelTraceId` (the trace at the level we
     *     drilled FROM, NOT necessarily the root trace)
     *   - `logicFlow == subFlowId`
     *   - sorted by `startedAt` desc, take first.
     *
     * Edge cases:
     *   - `parentLevelTraceId` null (drilling from a level that itself
     *     has no persisted trace) → cannot resolve, fall back to the
     *     `subFlowIoFromRoot` values for the matrix and clear step
     *     traces; the warning chip is shown.
     *   - QL returns no rows → same fallback as above.
     *   - QL fails → same fallback as above.
     */
    // TM-1000001563: resolve the child LogicFlowTrace for the active sub-flow level.
    private resolveLevelTrace(
        parentLevelTraceId: number | null,
        subFlowId: number,
        fallbackInputs: Record<string, any>,
        fallbackOutputs: Record<string, any>,
    ): void {
        // Defensive fallback when there is no parent level trace id —
        // happens at depth > 1 where the parent level is itself a
        // sub-flow without a persisted trace.
        if (parentLevelTraceId == null) {
            this.applyMissingLevelTrace();
            return;
        }

        const traceCrud = this.coreService.getCrudService('lg.LogicFlowTrace');
        const traceListRequest = ObjectRequestList.createBasic(
            true,
            [
                FilterCriteria.create('parentTrace', FilterCriteria.cOperatorEqual, parentLevelTraceId),
                FilterCriteria.create('logicFlow', FilterCriteria.cOperatorEqual, subFlowId),
            ],
            [new Sorting('startedAt', false)],
        );
        const traceListSub = traceCrud.list(traceListRequest).subscribe({
            next: (result: DtoList<LogicFlowTraceDto>) => {
                const childTrace = (result?.entries ?? [])[0] as LogicFlowTraceDto | undefined;
                if (!childTrace || childTrace.id == null) {
                    // No persisted child trace — leave the matrix +
                    // counter on the parent level (see
                    // applyMissingLevelTrace) and let the template
                    // surface the warning chip.
                    this.applyMissingLevelTrace();
                    return;
                }
                this.applyResolvedLevelTrace(childTrace, fallbackInputs, fallbackOutputs);
            },
            error: () => {
                // QL failure → behave as if no row was returned. The
                // warning chip surfaces; user can refresh to retry.
                this.applyMissingLevelTrace();
            },
        });
        this.subscriptions.push(traceListSub);
    }

    /**
     * @ticket TM-1000001563 (Issue 5)
     * Apply a successfully resolved child trace to the per-level matrix
     * + counter fields. Loads the child's step traces and refreshes the
     * counter widget filter to the child trace id.
     */
    // TM-1000001563: apply resolved child trace to level* fields + load steps.
    private applyResolvedLevelTrace(
        childTrace: LogicFlowTraceDto,
        fallbackInputs: Record<string, any>,
        fallbackOutputs: Record<string, any>,
    ): void {
        this.levelTrace = childTrace;
        this.levelTraceId = childTrace.id ?? null;
        this.levelTraceMissing = false;

        // Prefer the child trace's own flow-IO over the fallback when
        // they are populated; the fallback only kicks in for depth > 1
        // (where neither is persisted) or when the child has no IO JSON.
        const childInputs = this.safeParseJson(childTrace.flowInputsJson);
        const childOutputs = this.safeParseJson(childTrace.flowOutputsJson);
        this.levelFlowInputs = Object.keys(childInputs).length > 0 ? childInputs : fallbackInputs;
        this.levelFlowOutputs = Object.keys(childOutputs).length > 0 ? childOutputs : fallbackOutputs;

        // Load the child's step traces — same shape conversion as root.
        const stepsRequest = ObjectRequestList.createBasic(
            true,
            [FilterCriteria.create('trace', FilterCriteria.cOperatorEqual, childTrace.id)],
            [new Sorting('executionOrder', true)],
        );
        const stepsCrud = this.coreService.getCrudService('lg.LogicFlowTraceStep');
        const stepsSub = stepsCrud.list(stepsRequest).subscribe({
            next: (result: DtoList<LogicFlowTraceStepDto>) => {
                const childSteps = (result?.entries ?? []) as LogicFlowTraceStepDto[];
                this.levelStepTraces = this.mapTraceSteps(childSteps);
            },
            error: () => {
                this.levelStepTraces = [];
            },
        });
        this.subscriptions.push(stepsSub);

        // Re-build the counter widget against the new level trace id so
        // the per-Logic counters reflect ONLY the sub-flow's invocations.
        this.refreshCounterWidget();
    }

    /**
     * @ticket TM-1000001563 (Issue 5, Bug C1 follow-up)
     * Apply the "no persisted child trace" fallback. The previous version
     * of this method mutated `levelTrace` / `levelTraceId` /
     * `levelStepTraces` / `levelFlowInputs` / `levelFlowOutputs` to point
     * at the SUB_FLOW step's IO snapshot — which made the variable matrix
     * lie about which level it was showing (the chip claimed the canvas
     * was on the sub-flow, but the matrix was rendering parent-level data
     * relabelled as the child).
     *
     * The fix: when we cannot resolve a child LogicFlowTrace, we simply
     * leave the per-level matrix fields at whatever the parent level had.
     * The user can clearly see the matrix is still on the parent (the
     * `matrixFlowName` getter and the `(flowName)` suffix on the panel
     * heading reflect this), and the warning chip in the template makes
     * it explicit that no detailed protocol is available for the
     * sub-flow level. The counter widget's `levelTraceId` filter also
     * stays at the parent level's value, so the counters keep reporting
     * the parent's invocations rather than going blank.
     */
    // TM-1000001563: fallback when no child LogicFlowTrace exists.
    private applyMissingLevelTrace(): void {
        // Intentionally do NOT touch levelTrace / levelTraceId /
        // levelStepTraces / levelFlowInputs / levelFlowOutputs here —
        // they keep the parent level's values so the matrix and counter
        // continue to render the parent's data. Only the warning flag
        // changes so the template can surface the missing-trace chip.
        this.levelTraceMissing = true;
    }

    /**
     * @ticket TM-1000001563
     * Look up the values that were passed into / out of a SUB_FLOW step
     * during the root trace's execution. The data is recorded on the
     * matching `LogicFlowTraceStep` row's `inputDataJson` /
     * `outputDataJson`, which `mapTraceSteps` parsed into the
     * `inputs` / `outputs` records on `traceStepTraces`.
     */
    // TM-1000001563: source sub-flow flow-IO from the parent SUB_FLOW step's trace.
    private subFlowIoFromRoot(stepDefId: number, kind: 'inputs' | 'outputs'): Record<string, any> {
        const stepTrace = this.traceStepTraces?.find(t => t.stepId === stepDefId);
        if (!stepTrace) return {};
        const data = kind === 'inputs' ? stepTrace.inputs : stepTrace.outputs;
        return (data as Record<string, any> | null | undefined) ?? {};
    }

    /**
     * @ticket TM-1000001563
     * Apply a loaded flow definition to the per-level `current*` fields.
     * Shared by `navigateToRoot` and `drillIntoSubFlow` so the apply path
     * is identical.
     */
    // TM-1000001563: assign current* fields from a loaded FlowDefinition.
    private applyFlowDefinitionToCurrent(def: FlowDefinition): void {
        this.currentFlow = def.flow;
        this.currentFlowSteps = def.steps;
        this.currentResolvedSteps = def.resolvedSteps;
        this.currentOverrideMap = def.overrideMap;
        this.currentFlowImports = def.imports;
        this.currentFlowExports = def.exports;
        this.currentDeclarations = def.declarations;
        this.currentDeclarationsExist = def.declarationsExist;

        // TM-1000001563: rebuild alias maps for the new level so canvas
        // glow + inline sidebar lists are correct.
        this.rebuildAliasMaps();

        // TM-1000001563: refresh per-step bindings for the active flow so
        // the sidebar shows the correct declared inputs/outputs.
        if (def.flow.id != null) {
            this.loadStepBindingsForFlow(def.flow.id);
        }
    }

    /**
     * @ticket TM-1000001563
     * Look up a step definition by its global LogicFlowStep id. Searches the
     * ROOT flow's steps first, then every cached sub-flow's steps. This
     * lets the tree drill into any node regardless of how deeply nested
     * its `LogicFlowStep` lives in the loaded definition cache.
     */
    // TM-1000001563: lookup a step def across all cached flow definitions.
    private findStepDefById(stepDefId: number): LogicFlowStepDto | null {
        // Root flow first — common case.
        const inRoot = this.flowSteps?.find(s => s.id === stepDefId);
        if (inRoot) return inRoot;
        for (const def of this.flowDefinitionCache.values()) {
            const hit = def.steps.find(s => s.id === stepDefId);
            if (hit) return hit;
        }
        return null;
    }

    /**
     * @ticket TM-1000001563
     * Empty placeholder used when a trace's flow id is missing — the canvas
     * still applies a level so the chip / tree / variable store stay in
     * a consistent state instead of dangling on the previous level.
     */
    // TM-1000001563: defensive empty definition for traces without a flow id.
    private makeEmptyFlowDefinition(): FlowDefinition {
        return {
            flow: {id: 0} as LogicFlowDto,
            steps: [],
            resolvedSteps: [],
            overrideMap: undefined,
            imports: [],
            exports: [],
            declarations: [],
            declarationsExist: false,
        };
    }

    /**
     * @ticket TM-1000001563
     * Atomic per-flowId loader. Issues 6 parallel fetches (entity, steps,
     * imports, exports, resolved-inheritance, declarations); `done` is gated
     * on every fetch's completion via a `maybeDone()` counter, so the canvas
     * never sees a half-loaded mix of root and sub-flow data.
     */
    // TM-1000001563: 6-call parallel atomic load with cache reuse.
    private loadFlowDefinition(flowId: number, done: (def: FlowDefinition) => void): void {
        const cached = this.flowDefinitionCache.get(flowId);
        if (cached) {
            done(cached);
            return;
        }
        if (this.loadingFlowIds.has(flowId)) {
            // Already in flight — drop. The first completion will hydrate the
            // cache; subsequent navigations will pick it up immediately.
            return;
        }
        this.loadingFlowIds.add(flowId);

        let flowEntity: LogicFlowDto | null = null;
        let stepList: LogicFlowStepDto[] = [];
        let importList: LogicFlowImportDto[] = [];
        let exportList: LogicFlowExportDto[] = [];
        let resolvedList: ResolvedFlowStep[] = [];
        let overrideMapValue: Map<number, number> | undefined;
        let declarationList: LogicFlowResolvedDeclarationDto[] = [];

        let flowDone = false;
        let stepsDone = false;
        let importsDone = false;
        let exportsDone = false;
        let resolvedDone = false;
        let declarationsDone = false;

        const maybeDone = () => {
            if (!(flowDone && stepsDone && importsDone && exportsDone && resolvedDone && declarationsDone)) {
                return;
            }
            this.loadingFlowIds.delete(flowId);
            const def: FlowDefinition = {
                flow: flowEntity ?? ({id: flowId} as LogicFlowDto),
                steps: stepList,
                resolvedSteps: resolvedList,
                overrideMap: overrideMapValue,
                imports: importList,
                exports: exportList,
                declarations: declarationList,
                declarationsExist: declarationList.length > 0,
            };
            this.flowDefinitionCache.set(flowId, def);
            // TM-1000001563: a new sub-flow definition just landed in the
            // cache. Rebuild the memoized tree adapter so any caret-expand
            // node previously rendered as a placeholder now reflects the
            // sub-flow's real SUB_FLOW step children.
            this.recomputeTreeData();
            done(def);
        };

        // 1. Flow entity
        const flowCrud = this.coreService.getCrudService('lg.LogicFlow');
        const flowSub = flowCrud.get(flowId).subscribe({
            next: (result: LogicFlowDto) => {
                flowEntity = result ?? null;
                flowDone = true;
                maybeDone();
            },
            error: () => {
                flowEntity = null;
                flowDone = true;
                maybeDone();
            }
        });
        this.subscriptions.push(flowSub);

        // 2. Step list
        const stepsRequest = ObjectRequestList.createBasic(
            true,
            [FilterCriteria.create('logicFlow', FilterCriteria.cOperatorEqual, flowId)],
            [new Sorting('orderPriority', true)]
        );
        const stepsCrud = this.coreService.getCrudService('lg.LogicFlowStep');
        const stepsSub = stepsCrud.list(stepsRequest).subscribe({
            next: (result: DtoList<LogicFlowStepDto>) => {
                stepList = (result?.entries ?? []) as LogicFlowStepDto[];
                stepsDone = true;
                maybeDone();
            },
            error: () => {
                stepList = [];
                stepsDone = true;
                maybeDone();
            }
        });
        this.subscriptions.push(stepsSub);

        // 3. Declared imports
        const importsRequest = ObjectRequestList.createBasic(
            true,
            [FilterCriteria.create('logicFlow', FilterCriteria.cOperatorEqual, flowId)],
            [new Sorting('variableName', true)]
        );
        const importsCrud = this.coreService.getCrudService('lg.LogicFlowImport');
        const importsSub = importsCrud.list(importsRequest).subscribe({
            next: (result: DtoList<LogicFlowImportDto>) => {
                importList = (result?.entries ?? []) as LogicFlowImportDto[];
                importsDone = true;
                maybeDone();
            },
            error: () => {
                importList = [];
                importsDone = true;
                maybeDone();
            }
        });
        this.subscriptions.push(importsSub);

        // 4. Declared exports
        const exportsRequest = ObjectRequestList.createBasic(
            true,
            [FilterCriteria.create('logicFlow', FilterCriteria.cOperatorEqual, flowId)],
            [new Sorting('variableName', true)]
        );
        const exportsCrud = this.coreService.getCrudService('lg.LogicFlowExport');
        const exportsSub = exportsCrud.list(exportsRequest).subscribe({
            next: (result: DtoList<LogicFlowExportDto>) => {
                exportList = (result?.entries ?? []) as LogicFlowExportDto[];
                exportsDone = true;
                maybeDone();
            },
            error: () => {
                exportList = [];
                exportsDone = true;
                maybeDone();
            }
        });
        this.subscriptions.push(exportsSub);

        // 5. Resolved inheritance
        const resolveSub = this.inheritanceResolver.resolve(flowId).subscribe({
            next: (resolved) => {
                resolvedList = resolved?.steps ?? [];
                overrideMapValue = resolved?.overrideMap;
                resolvedDone = true;
                maybeDone();
            },
            error: () => {
                resolvedList = [];
                overrideMapValue = undefined;
                resolvedDone = true;
                maybeDone();
            }
        });
        this.subscriptions.push(resolveSub);

        // 6. Resolved declarations
        const declSub = this.inheritanceResolver.resolveDeclarations(flowId).subscribe({
            next: (decls) => {
                declarationList = decls ?? [];
                declarationsDone = true;
                maybeDone();
            },
            error: () => {
                declarationList = [];
                declarationsDone = true;
                maybeDone();
            }
        });
        this.subscriptions.push(declSub);
    }

    /**
     * @ticket TM-1000001563
     * Defect 2 — rebuild the alias-bookkeeping maps for the ACTIVE level.
     * Defensive: only emits when both sides (currentResolvedSteps +
     * currentDeclarations) have at least one entry; before that the maps
     * stay empty.
     */
    // TM-1000001563: per-level alias bookkeeping rebuild.
    private rebuildAliasMaps(): void {
        if (!this.currentResolvedSteps?.length || !this.currentDeclarations) {
            this.aliasMaps = {
                calledAliasesByStep: new Map<number, Set<string>>(),
                dynamicAliasCallsByStep: new Map<number, number>(),
                aliasToCallingStepIds: new Map<string, Set<number>>(),
            };
            this.currentCallingStepIdsForAlias = this.selectedAlias ? new Set<number>() : null;
            return;
        }
        this.aliasMaps = buildAliasMaps(this.currentResolvedSteps, this.currentDeclarations);
        // Refresh the alias-glow set against the new level's index.
        this.currentCallingStepIdsForAlias = this.selectedAlias
            ? this.aliasMaps.aliasToCallingStepIds.get(this.selectedAlias) ?? new Set<number>()
            : null;
    }

    /**
     * @ticket TM-1000001563
     * Defect 3 — load all step bindings of a flow so the sidebar can render
     * declared inputs/outputs by name. Mirrors the test-runner exactly so both
     * surfaces share the same data shape and bindingName dedupe behaviour.
     */
    // TM-1000001563: per-flow step bindings — feeds the extracted step-detail sidebar.
    private loadStepBindingsForFlow(flowId: number): void {
        if (flowId == null) {
            this.stepBindingsByStepId = new Map();
            return;
        }
        // TM-1000001563: QL validator false positive — `flowStep` join field exists on
        // entity, see com.mvs.backend.lg.model.LogicFlowStepBinding.flowStep (line 87);
        // alias `fs` is declared via the join's `as` value below.
        const sub = this.qlService.builder()
            .query({
                name: 'flowStepBindings',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'lg.LogicFlowStepBinding',
                    as: 'e',
                    joins: [
                        {
                            type: 'entity',
                            name: 'flowStep',
                            as: 'fs',
                            joinType: 'inner',
                            filterCriteria: [
                                FilterCriteria.create('fs.logicFlowId', FilterCriteria.cOperatorEqual, flowId)
                            ]
                        }
                    ]
                }
            })
            .pipeType('object')
            .execute()
            .subscribe({
                next: (result) => {
                    const rawRows = (result.get('flowStepBindings') as FlowStepBindingRawRow[]) ?? [];
                    const grouped = new Map<number, LogicFlowStepBindingDto[]>();
                    for (const raw of rawRows) {
                        // TM-1000001563: map backend field names → frontend DTO field names.
                        const binding = new LogicFlowStepBindingDto();
                        binding.id = raw.id;
                        binding.flowStepDtoId = raw.flowStepId;
                        binding.directionEnum = raw.direction as unknown as BindingDirectionEnum;
                        binding.bindingName = raw.variableName;

                        const stepId = binding.flowStepDtoId;
                        if (stepId == null) continue;
                        const bucket = grouped.get(stepId) ?? [];
                        bucket.push(binding);
                        grouped.set(stepId, bucket);
                    }
                    this.stepBindingsByStepId = grouped;
                },
                error: () => {
                    // TM-1000001563: soft-fail — sidebar simply renders empty rows.
                    this.stepBindingsByStepId = new Map();
                }
            });
        this.subscriptions.push(sub);
    }

    /**
     * @ticket TM-1000001563
     * Map persisted LogicFlowTraceStep rows into the canvas-friendly
     * LgFlowStepTraceDto shape. Extracted so both root and sub-flow level
     * loads share the same conversion path.
     */
    // TM-1000001563: trace-step → canvas DTO converter (level-agnostic).
    private mapTraceSteps(steps: LogicFlowTraceStepDto[]): LgFlowStepTraceDto[] {
        return (steps ?? []).map<LgFlowStepTraceDto>(s => ({
            stepId: s.flowStepDtoId ?? 0,
            stepName: s.flowStepDto?.name ?? s.flowStepDtoName ?? '',
            stepOrder: s.flowStepDto?.orderPriority ?? 0,
            stepType: String(s.flowStepDto?.stepType ?? ''),
            executionOrder: s.executionOrder ?? 0,
            status: this.mapStepStatus(s.stepExecutionStatus),
            durationMs: s.durationMs ?? 0,
            inputs: this.safeParseJson(s.inputDataJson),
            outputs: this.safeParseJson(s.outputDataJson),
            errorMessage: s.errorMessage ?? '',
            conditionResult: !!s.conditionResult,
            failedExpectations: []
        }));
    }

    /**
     * Converts the persisted `stepExecutionStatus` into the enum NAME string
     * ('COMPLETED', 'FAILED', 'SKIPPED', ...).
     */
    private mapStepStatus(raw: LogicFlowStepExecutionStatusEnum | undefined | null): string {
        if (raw == null) {
            return '';
        }
        if (typeof raw === 'number') {
            return LogicFlowStepExecutionStatusEnum[raw] ?? '';
        }
        return String(raw);
    }

    /** Defensive JSON parser. */
    private safeParseJson(raw: string | null | undefined): Record<string, any> {
        if (raw == null || raw === '') {
            return {};
        }
        try {
            const parsed = JSON.parse(raw);
            return parsed ?? {};
        } catch {
            return {};
        }
    }

    /**
     * Build the per-Logic counter widget for the ACTIVE canvas level.
     *
     * @ticket TM-1000001563 (Issue 5)
     * Previously this widget was hard-bound to `this.traceId` — the root
     * input — so the counters never followed a SUB_FLOW drill-in. It now
     * parameterizes by `levelTraceId`, which is set to the root trace id
     * at root and to the resolved child trace's id after a drill. Called
     * whenever the level changes (root seed, drill-in, drill-out).
     */
    // TM-1000001563 (Issue 5): per-level counter widget — filters on levelTraceId.
    refreshCounterWidget(): void {
        // TM-1000001563 (Issue 5): null levelTraceId means we are on a
        // sub-flow level without persisted trace data — clear the
        // counter widget so the template hides it.
        if (this.levelTraceId == null) {
            this.counterWidget = null;
            return;
        }

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: Runtime
         * Reason: Table widget listing LogicFlowTraceLogicCounter rows for the
         *         active canvas level's trace, joined with lg.Logic via the
         *         `logic` FK attribute to display the logic's name alongside
         *         counter metrics, sorted by invocation count descending.
         */
        const qlQueryDto: QlQueryDto = {
            name: 'lg.flow.logicFlowTraceLogicCounter.table.per-logic',
            type: 'view',
            start: {
                type: 'entity',
                name: 'lg.LogicFlowTraceLogicCounter',
                as: 'e',
                joins: [
                    {
                        type: 'entity',
                        name: 'logic',
                        as: 'l',
                        joinType: 'left',
                        fields: [
                            {name: 'name'}
                        ]
                    }
                ]
            },
            selectAttributes: [],
            fixedFilters: [
                // TM-1000001563 (Issue 5): filter by levelTraceId (root or child).
                FilterCriteria.create('e.traceId', FilterCriteria.cOperatorEqual, this.levelTraceId)
            ],
            sortings: [new Sorting('e.invocationCount', false)]
        };

        /** AI: Status: 'in progress', Type: Widget, SubType: Runtime, Reason: Table widget showing per-Logic execution counter for the active level's trace (QL). */
        this.counterWidget = WidgetFactory.createWidgetTableQl(
            'lg.flow.logicFlowTraceLogicCounter.table.per-logic',
            'Logik-Zähler',
            qlQueryDto
        );
    }

    /**
     * @ticket TM-1000001563
     * Toggle the right-hand declaration rail open / closed.
     */
    // TM-1000001563: handler for the canvas toolbar 'Deklarationen' toggle
    protected toggleDeclarationsView(): void {
        this.declarationsViewOpen = !this.declarationsViewOpen;
    }

    // ----------------------------------------------------------------
    // TM-1000001563: alias rail handlers (Defect 2 wiring)
    // ----------------------------------------------------------------

    /**
     * @ticket TM-1000001563
     * Card click on the rail. Toggles the selection: clicking the already
     * selected alias clears the canvas glow, otherwise selects the new alias.
     */
    // TM-1000001563: alias rail click handler — drives the canvas glow.
    protected onAliasSelected(alias: string): void {
        this.selectedAlias = this.selectedAlias === alias ? null : alias;
        // Resolve glow set against the active level's index.
        this.currentCallingStepIdsForAlias = this.selectedAlias
            ? this.aliasMaps.aliasToCallingStepIds.get(this.selectedAlias) ?? new Set<number>()
            : null;
    }

    /**
     * @ticket TM-1000001563
     * Cmd/Ctrl-click on a rail card — open the underlying lg.Logic in the
     * standard right-drawer navigation panel. Mirrors the page's handler so
     * navigation works identically on both surfaces.
     */
    // TM-1000001563: alias rail open-logic handler — global drawer navigation.
    protected onOpenLogic(event: { logicId: number; alias: string }): void {
        if (!event || event.logicId == null) {
            return;
        }
        const navEntry = MvsObjectNavigationEntry.createNavigationEntry(
            'lg.Logic',
            event.logicId,
            null,
            'Object',
            null,
            null,
            MvsObjectNavigationActionEnum.any,
        );
        this.navigationService.navigateTo(navEntry, 'right');
    }

    // ----------------------------------------------------------------
    // TM-1000001563: step-detail sidebar handlers (Defect 3 wiring)
    // ----------------------------------------------------------------

    /**
     * @ticket TM-1000001563
     * Designer step-click handler — drills into a SUB_FLOW step (loading
     * the sub-flow's static definition; no persisted child trace required)
     * or opens the step-detail sidebar for any other step type.
     */
    // TM-1000001563: SUB_FLOW drill-in or open the sidebar for a step.
    protected onStepClicked(stepTrace: LgFlowStepTraceDto): void {
        if (!stepTrace || stepTrace.stepId == null) return;
        const def = this.currentFlowSteps?.find(s => s.id === stepTrace.stepId) ?? null;

        if (def
            && def.stepType === LogicFlowStepTypeEnum.SUB_FLOW
            && def.subFlowDtoId != null
            && def.id != null
        ) {
            // TM-1000001563: drill into the sub-flow. Close the sidebar so
            // we don't carry the parent step's selection into the new level.
            this.selectedStepTrace = null;
            this.selectedStepDef = null;
            this.sidebarOpen = false;
            this.sidebarMode = 'step';
            this.selectedFlowIo = null;
            this.drillIntoSubFlow(def.id);
            return;
        }

        // Fallback: open the sidebar with the step's trace values.
        this.selectedStepTrace = stepTrace;
        this.selectedStepDef = def;
        this.selectedFlowIo = null;
        this.sidebarOpen = true;
        this.sidebarMode = 'step';
    }

    /**
     * @ticket TM-1000001563
     * Designer step-def-click handler — for steps that did NOT execute. Opens
     * the sidebar with placeholder values (no trace).
     */
    // TM-1000001563: open the sidebar for a step that did not run.
    protected onSelectedStepDef(step: LogicFlowStepDto): void {
        if (!step) return;
        this.selectedStepDef = step;
        // TM-1000001563: pair with matching trace on the active level if any.
        // Use `levelStepTraces` (always populated for the active canvas level —
        // root trace at root, child trace after sub-flow drill-in) instead of
        // `currentStepTraces`, which is reset to [] when drilling into a
        // sub-flow and is therefore empty on every sub-level click. The
        // designer fires both `stepClicked` and `stepDefClicked` on every
        // click; without this fix the second event would null out the
        // value-carrying trace already assigned by `onStepClicked`.
        const matchingTrace = this.levelStepTraces?.find(t => t.stepId === step.id) ?? null;
        this.selectedStepTrace = matchingTrace;
        this.selectedFlowIo = null;
        this.sidebarOpen = true;
        this.sidebarMode = 'step';
    }

    /**
     * @ticket TM-1000001563
     * Designer flow-I/O click handler — opens the flow-I/O panel for the
     * START / ENDE pseudo-circle clicked on the canvas.
     */
    // TM-1000001563: open the flow-I/O detail panel for START / ENDE clicks.
    protected onFlowIoClicked(event: LgFlowIoSelection): void {
        // TM-1000001563: mutually exclusive with the step sidebar.
        this.selectedStepTrace = null;
        this.selectedStepDef = null;
        this.sidebarOpen = false;
        this.selectedFlowIo = event;
    }

    /**
     * @ticket TM-1000001563
     * Closes the step-detail sidebar.
     */
    // TM-1000001563: dismiss the sidebar (close button + side-effect on toggle).
    protected closeStepDetail(): void {
        this.selectedStepTrace = null;
        this.selectedStepDef = null;
        this.sidebarOpen = false;
        this.sidebarMode = 'step';
    }

    /** Closes the flow-I/O detail panel. */
    // TM-1000001563: dismiss the flow-I/O detail panel (close button).
    protected closeFlowIoDetail(): void {
        this.selectedFlowIo = null;
    }

    /**
     * @ticket TM-1000001563
     * Sidebar mode change handler — bubbled up from the embedded sidebar.
     */
    // TM-1000001563: forward sidebar mode toggles into local sidebarMode.
    protected onSidebarModeChange(mode: 'step' | 'declarations'): void {
        this.sidebarMode = mode;
    }

    /**
     * @ticket TM-1000001563
     * Plain click on an inline declaration row inside the sidebar.
     * Cmd/Ctrl-click → open underlying Logic. Plain click → flip mode +
     * select alias for canvas glow.
     */
    // TM-1000001563: inline declaration click (sidebar passes the original event).
    protected onInlineDeclarationClicked(decl: LogicFlowResolvedDeclarationDto, event: MouseEvent): void {
        if (event.metaKey === true || event.ctrlKey === true) {
            event.preventDefault();
            event.stopPropagation();
            this.onOpenLogic({logicId: decl.logicId, alias: decl.alias});
            return;
        }
        this.onAliasSelected(decl.alias);
        this.sidebarMode = 'declarations';
    }

    /**
     * @ticket TM-1000001563
     * Middle-click on an inline declaration row — open underlying Logic.
     */
    // TM-1000001563: middle-click on inline declaration → global Logic drawer.
    protected onInlineDeclarationAuxClicked(decl: LogicFlowResolvedDeclarationDto, event: MouseEvent): void {
        if (event.button !== 1) return;
        event.preventDefault();
        event.stopPropagation();
        this.onOpenLogic({logicId: decl.logicId, alias: decl.alias});
    }

    // ----------------------------------------------------------------
    // TM-1000001563: TREE PANEL HANDLERS
    // ----------------------------------------------------------------

    /**
     * @ticket TM-1000001563
     * Tree-node single-click handler. The tree's `executionId` is overloaded
     * for this surface:
     *   - If `id === rootTraceId` → navigate to the ROOT level.
     *   - Otherwise the id is a `LogicFlowStep.id` (a SUB_FLOW step) and we
     *     drill into the sub-flow it references.
     */
    // TM-1000001563: tree single-click → navigate to root or drill into sub-flow.
    protected onTreeTraceSelected(id: number): void {
        if (id === this.rootTraceId) {
            // TM-1000001563: clicking the root node returns the canvas to
            // root level. Reset viaStepStack so the chip disappears.
            this.viaStepStack = [];
            // TM-1000001563 (Issue 5): same synchronous root restore as
            // the "Zurück" chip — keeps the matrix + counter snappy.
            this.restoreRootMatrixSnapshot();
            this.navigateToRoot();
            return;
        }
        // Sub-flow node — id is the SUB_FLOW step's LogicFlowStep.id. Reset
        // the via-stack so the path matches the click target rather than
        // appending (the user may click any node, not strictly drill in).
        const stepDef = this.findStepDefById(id);
        if (!stepDef || stepDef.subFlowDtoId == null) return;
        this.viaStepStack = [];
        this.drillIntoSubFlow(id);
    }

    /**
     * @ticket TM-1000001563
     * Tree caret-expand handler — pre-fetch the sub-flow definition for the
     * given step so the tree's grandchild SUB_FLOW step nodes can be
     * derived from its loaded step list. Does NOT navigate the canvas.
     */
    // TM-1000001563: tree caret-expand → lazy-load sub-flow definition only.
    protected onTreeExpandRequested(id: number): void {
        if (id === this.rootTraceId) return; // root flow is already loaded
        const stepDef = this.findStepDefById(id);
        if (!stepDef || stepDef.subFlowDtoId == null) return;
        if (this.flowDefinitionCache.has(stepDef.subFlowDtoId)) return;
        // Load + cache; the tree adapter re-derives once cache is hot.
        this.loadFlowDefinition(stepDef.subFlowDtoId, () => {
            // No-op: the getter will pick up the cached value on next CD.
        });
    }

    // ----------------------------------------------------------------
    // TM-1000001563: SUB-FLOW CHIP HANDLERS
    // ----------------------------------------------------------------

    /** True while the canvas is inside a sub-flow level. */
    // TM-1000001563: chip visibility flag.
    get isOnSubFlow(): boolean {
        return this.viaStepStack.length > 0;
    }

    /** Display name of the active level's flow. */
    // TM-1000001563: chip label.
    get currentFlowName(): string {
        return this.currentFlow?.name ?? '';
    }

    /**
     * Display name for the variable matrix, surfaced in the panel
     * heading as "Variablenverlauf (flowName)". Always reflects the
     * level whose data the matrix is actually showing — see
     * `applyMissingLevelTrace` for the no-detail-sub-flow case where
     * the matrix stays on the parent level.
     *
     * @ticket TM-1000001563
     */
    protected get matrixFlowName(): string | undefined {
        // Prefer the levelTrace's flow name; fall back to the original
        // root trace's flow name if levelTrace is null (matrix on root).
        return this.levelTrace?.logicFlowDtoName
            ?? this.trace?.logicFlowDtoName;
    }

    /**
     * @ticket TM-1000001563
     * Whether the Ausführungsbaum (execution-tree) overlay should be visible.
     * The tree appears as soon as the root trace ran at least one SUB_FLOW
     * step — built from the parent flow's step definitions filtered to
     * SUB_FLOW step types intersected with `traceStepTraces`.
     */
    // TM-1000001563: execution-tree visibility gate (root has ≥1 SUB_FLOW step trace).
    get treePanelVisible(): boolean {
        if (!this.flowSteps?.length || !this.traceStepTraces?.length) return false;
        const ranStepIds = new Set<number>(this.traceStepTraces.map(t => t.stepId));
        return this.flowSteps.some(
            s => s.stepType === LogicFlowStepTypeEnum.SUB_FLOW
                && s.id != null
                && ranStepIds.has(s.id)
        );
    }

    /**
     * @ticket TM-1000001563
     * Identifier of the tree node that should be highlighted as 'selected'
     * for the active canvas level.
     *   - On the root level → the root LogicFlowTrace.id.
     *   - On a sub-flow level → the LogicFlowStep.id of the deepest SUB_FLOW
     *     step we drilled through (top of `viaStepStack`).
     *
     * Returning a primitive (number | null) is safe to compute as a getter:
     * it never produces a fresh reference, so the OnPush tree-panel only
     * sees a real change when the value actually changes.
     */
    // TM-1000001563: synced selection key for [currentExecutionId] binding.
    get treeSelectedNodeId(): number | null {
        if (this.viaStepStack.length === 0) return this.rootTraceId;
        return this.viaStepStack[this.viaStepStack.length - 1];
    }

    /**
     * @ticket TM-1000001563
     * 'Zurück' chip handler — pop one sub-flow level. If the stack empties,
     * navigate back to the root level.
     *
     * @ticket TM-1000001563 (Issue 5)
     * When popping all the way back to root, restore the per-level
     * matrix + counter fields from the cached `rootMatrixSnapshot`
     * synchronously so the variable matrix and the counter widget
     * snap back to root data immediately, without waiting for the
     * async re-resolve inside `navigateToRoot()`.
     */
    // TM-1000001563: pop one sub-flow level back toward the root.
    protected onNavigateUpOneLevel(): void {
        if (this.viaStepStack.length === 0) return;
        const popped = this.viaStepStack.slice(0, -1);
        if (popped.length === 0) {
            // Back to root.
            this.viaStepStack = [];
            // TM-1000001563 (Issue 5): restore level* fields from
            // cached root snapshot before kicking off navigateToRoot()
            // so the matrix + counter update synchronously. The
            // subsequent navigateToRoot() will re-assert the same
            // values once its async fetches resolve.
            this.restoreRootMatrixSnapshot();
            this.navigateToRoot();
            return;
        }
        // Go to the level identified by the new top of stack.
        const targetStepId = popped[popped.length - 1];
        const stepDef = this.findStepDefById(targetStepId);
        if (!stepDef || stepDef.subFlowDtoId == null) return;
        // The drill helper will push the targetStepId again, so reset to
        // popped-without-last and let drillIntoSubFlow re-push.
        this.viaStepStack = popped.slice(0, -1);
        this.drillIntoSubFlow(targetStepId);
    }

    /**
     * @ticket TM-1000001563 (Issue 5)
     * Restore the per-level matrix + counter fields from the cached
     * root snapshot so a "Zurück" / tree-root click flips back to root
     * data instantly. Refreshes the counter widget so it filters by the
     * root trace id again.
     */
    // TM-1000001563: cheap synchronous restore of root level fields.
    private restoreRootMatrixSnapshot(): void {
        if (!this.rootMatrixSnapshot) return;
        this.levelTrace = this.rootMatrixSnapshot.trace;
        this.levelTraceId = this.rootMatrixSnapshot.traceId;
        this.levelStepTraces = this.rootMatrixSnapshot.stepTraces;
        this.levelFlowInputs = this.rootMatrixSnapshot.flowInputs;
        this.levelFlowOutputs = this.rootMatrixSnapshot.flowOutputs;
        this.levelTraceMissing = false;
        this.refreshCounterWidget();
    }

    /**
     * @ticket TM-1000001563
     * MEMOIZED: synthetic root node for <mvs-lg-flow-execution-tree-panel>.
     * Stable reference until `recomputeTreeData()` runs. Bound directly into
     * `[rootTrace]` — using a getter here would cause an infinite re-render
     * loop because the tree-panel is OnPush and rebuilds on every input
     * change; a getter creates a fresh DTO on every CD pass.
     */
    // TM-1000001563: memoized root tree node (stable reference).
    protected treeRootResult: LgFlowTraceResultDto | null = null;

    /**
     * @ticket TM-1000001563
     * MEMOIZED: synthetic `childTraceCache` for the tree panel. Stable
     * reference until `recomputeTreeData()` runs. Same loop-prevention
     * rationale as `treeRootResult` above.
     */
    // TM-1000001563: memoized tree child cache (stable reference).
    protected treeChildCache: Map<number, LgFlowTraceResultDto> = new Map();

    /**
     * @ticket TM-1000001563
     * Recomputes `treeRootResult` and `treeChildCache` from the current
     * `trace` / `flowSteps` / `traceStepTraces` / `flowDefinitionCache`.
     * Call this after any of those underlying values change (root load,
     * sub-flow drill, sub-flow definition cache hit). Idempotent — safe to
     * call repeatedly; the OnPush tree-panel only rebuilds when references
     * actually change.
     */
    // TM-1000001563: rebuild tree adapter fields when underlying state changes.
    private recomputeTreeData(): void {
        if (!this.trace || this.trace.id == null
            || !this.flowSteps?.length || !this.traceStepTraces?.length) {
            this.treeRootResult = null;
            this.treeChildCache = new Map();
            return;
        }

        const subFlowSteps = this.collectExecutedSubFlowSteps(this.flowSteps, this.traceStepTraces);

        // Root node — always non-null when we get here.
        this.treeRootResult = {
            // TM-1000001563: derive boolean success flag from the new ordinal
            // status enum (RUNNING / SUCCESS / FAILED) since LogicFlowTraceDto
            // no longer carries a boolean `success` field. Tree adapter still
            // expects a boolean here, so map only the SUCCESS ordinal to true.
            success: this.trace.status === LogicFlowTraceStatusEnum.SUCCESS,
            executionId: this.trace.id,
            totalDurationMs: this.trace.totalDurationMs ?? 0,
            errorMessage: this.trace.errorMessage ?? '',
            messages: [],
            stepTraces: [],
            failedExpectations: [],
            flowId: this.trace.logicFlowDtoId,
            flowName: this.trace.logicFlowDtoName,
            childExecutions: subFlowSteps.map(({stepDef, traceStep}) =>
                this.toChildExecutionSummary(stepDef, traceStep),
            ),
        };

        // Recursive cache — BFS over already-loaded sub-flow definitions so
        // the tree shows everything we currently know AND the carets remain
        // clickable for un-loaded sub-trees (handled in toChildExecutionSummary).
        const out = new Map<number, LgFlowTraceResultDto>();
        const queue: { stepDef: LogicFlowStepDto; traceStep: LgFlowStepTraceDto }[] =
            [...subFlowSteps];
        while (queue.length > 0) {
            const {stepDef, traceStep} = queue.shift()!;
            if (stepDef.id == null || stepDef.subFlowDtoId == null) continue;
            if (out.has(stepDef.id)) continue;

            const subFlowDef = this.flowDefinitionCache.get(stepDef.subFlowDtoId);
            const grandchildren: ChildExecutionSummaryDto[] = subFlowDef
                ? subFlowDef.steps
                    .filter(s => s.stepType === LogicFlowStepTypeEnum.SUB_FLOW
                        && s.subFlowDtoId != null && s.id != null)
                    .map(s => this.toChildExecutionSummary(s, null))
                : [];

            out.set(stepDef.id, {
                success: traceStep.status === 'COMPLETED',
                executionId: stepDef.id,
                totalDurationMs: traceStep.durationMs ?? 0,
                errorMessage: traceStep.errorMessage ?? '',
                messages: [],
                stepTraces: [],
                failedExpectations: [],
                flowId: stepDef.subFlowDtoId,
                flowName: stepDef.subFlowDtoName,
                childExecutions: grandchildren,
            });

            if (subFlowDef) {
                for (const child of subFlowDef.steps) {
                    if (child.stepType === LogicFlowStepTypeEnum.SUB_FLOW
                        && child.id != null
                        && child.subFlowDtoId != null) {
                        queue.push({stepDef: child, traceStep: this.placeholderTraceStep(child)});
                    }
                }
            }
        }
        this.treeChildCache = out;
    }

    /**
     * @ticket TM-1000001563
     * Filter step definitions to those that are SUB_FLOW AND were actually
     * executed in the trace (i.e. have a matching trace step). Returns the
     * pairs in the order the trace executed them.
     */
    // TM-1000001563: extract executed SUB_FLOW step pairs from a level.
    private collectExecutedSubFlowSteps(
        stepDefs: LogicFlowStepDto[],
        traceSteps: LgFlowStepTraceDto[],
    ): { stepDef: LogicFlowStepDto; traceStep: LgFlowStepTraceDto }[] {
        if (!stepDefs?.length || !traceSteps?.length) return [];
        const stepDefById = new Map<number, LogicFlowStepDto>();
        for (const def of stepDefs) {
            if (def.id != null) stepDefById.set(def.id, def);
        }
        const orderedTraceSteps = [...traceSteps]
            .sort((a, b) => (a.executionOrder ?? 0) - (b.executionOrder ?? 0));
        const out: { stepDef: LogicFlowStepDto; traceStep: LgFlowStepTraceDto }[] = [];
        for (const traceStep of orderedTraceSteps) {
            const def = stepDefById.get(traceStep.stepId);
            if (def?.stepType === LogicFlowStepTypeEnum.SUB_FLOW
                && def.subFlowDtoId != null
                && def.id != null) {
                out.push({stepDef: def, traceStep});
            }
        }
        return out;
    }

    /**
     * @ticket TM-1000001563
     * Build a `ChildExecutionSummaryDto` from a SUB_FLOW step definition.
     * If a matching trace step exists (root-level executed sub-flows) the
     * status/duration come from it. Otherwise the node represents an
     * un-executed deep-level sub-flow node — status defaults to "unknown".
     *
     * `hasChildExecutions` reflects whether the underlying sub-flow has
     * its OWN SUB_FLOW steps (lookup via the flow definition cache);
     * defaults to true when the sub-flow definition has not been loaded yet
     * so the caret stays clickable until expansion proves otherwise.
     */
    // TM-1000001563: SUB_FLOW step → synthetic child execution.
    private toChildExecutionSummary(
        stepDef: LogicFlowStepDto,
        traceStep: LgFlowStepTraceDto | null,
    ): ChildExecutionSummaryDto {
        const subFlowDef = stepDef.subFlowDtoId != null
            ? this.flowDefinitionCache.get(stepDef.subFlowDtoId)
            : undefined;
        const hasChildExecutions = subFlowDef != null
            ? subFlowDef.steps.some(s => s.stepType === LogicFlowStepTypeEnum.SUB_FLOW
                && s.subFlowDtoId != null
                && s.id != null)
            : true; // unknown until the sub-flow is loaded — keep caret.
        return {
            executionId: stepDef.id ?? undefined,
            logicFlowId: stepDef.subFlowDtoId,
            flowName: stepDef.subFlowDtoName,
            success: traceStep ? traceStep.status === 'COMPLETED' : undefined,
            durationMs: traceStep?.durationMs,
            hasChildExecutions,
        };
    }

    /**
     * @ticket TM-1000001563
     * Synthesise a placeholder trace step for tree levels that have no
     * persisted execution data (deep sub-flow nodes). Only the fields the
     * tree adapter actually reads are populated; everything else stays
     * defaulted.
     */
    // TM-1000001563: placeholder trace-step for un-executed deep tree levels.
    private placeholderTraceStep(stepDef: LogicFlowStepDto): LgFlowStepTraceDto {
        return {
            stepId: stepDef.id ?? 0,
            stepName: stepDef.name ?? '',
            stepOrder: stepDef.orderPriority ?? 0,
            stepType: String(stepDef.stepType ?? ''),
            executionOrder: 0,
            status: '',
            durationMs: 0,
            inputs: {},
            outputs: {},
            errorMessage: '',
            conditionResult: false,
            failedExpectations: [],
        };
    }
}
